import React from 'react';
import { EmbeddedPodletResponse } from './Podlets';
import { usePodletContent } from './usePodletContent';
import { useReloadPodlet } from './useReloadPodlet';

type EmbeddedPodletProps = {
    cautiouslyReloadScriptOnHydrationError?: boolean;
    className?: string;
    response: EmbeddedPodletResponse;
    suppressHydrationWarning?: boolean;
};

function EmbeddedPodletInner(
    props: EmbeddedPodletProps,
    forwardedRef: React.ForwardedRef<HTMLDivElement>,
) {
    const {
        cautiouslyReloadScriptOnHydrationError = false,
        className,
        response,
        suppressHydrationWarning = false,
    } = props;

    const { innerHTML, isHydrationErrorRerendering, setPodletRef } =
        usePodletContent(response);

    const { containerRef } = useReloadPodlet(
        response.js,
        cautiouslyReloadScriptOnHydrationError,
    );

    return (
        <div
            className={`${className} ${isHydrationErrorRerendering ? 'invisible' : ''}`}
            dangerouslySetInnerHTML={innerHTML}
            ref={(element) => {
                assignForwardedRef(forwardedRef, element);
                setPodletRef(element);
                if (isHydrationErrorRerendering) {
                    containerRef(element);
                }
            }}
            // This is a hack to suppress the React hydration warning that is
            // caused by the <template shadowrootmode="open"> element that is
            // used by the podlet. The issues are related to the fact that
            // <template shadowrootmode="open"> gets removed by supported
            // browsers when the HTML is parsed, causing React to complain
            // about mismatching DOM structures between the server and the
            // client. Works only in development mode.
            // See
            //   https://github.com/mfreed7/declarative-shadow-dom#-behavior
            suppressHydrationWarning={suppressHydrationWarning}
        ></div>
    );
}

function assignForwardedRef(
    forwardedRef: React.ForwardedRef<HTMLDivElement>,
    element: HTMLDivElement | null,
) {
    if (typeof forwardedRef === 'function') {
        forwardedRef(element);
    } else if (forwardedRef) {
        forwardedRef.current = element;
    }
}

export const EmbeddedPodlet = React.forwardRef(EmbeddedPodletInner);
